import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { keys, groupBy, isEmpty } from 'lodash';
import moment from 'moment';
import Message from './Message';
import { ChatMessage } from '../../../shared/models/chat';
import { GLYMPSE_PRO_CHAT_ORIGIN } from '../../../shared/constants/chat.constants';
import ScrollToBottomButton from './ScrollToBottomButton';
import useScrollBottom from '../../../shared/hooks/scrollBottom.hook';

interface MessageProps {
  messages: ChatMessage[];
}

function MessageList(props: MessageProps) {
  const {
    messages,
  } = props;

  const [isScrollToBottom, containerRef] = useScrollBottom();
  const messagesEndRef = useRef(null);

  const [isFirstOpen, setIsFirstOpen] = useState(true);

  const getMessageElement = (message) => {
    const isMine = message.origin === GLYMPSE_PRO_CHAT_ORIGIN;
    return (
      <Message
        key={message.id}
        isMine={isMine}
        message={message}
      />
    );
  };

  const messagesGroupedByDate = useMemo(() => {
    return groupBy(messages, (message: ChatMessage) => moment(message.created_time).format('ddd, MMM. Do'));
  }, [messages]);

  const scrollToBottomOfList = React.useCallback(() => {
    messagesEndRef!.current!.scrollIntoView!({
      behavior: "auto",
      block: "end"
    });
  }, [messagesEndRef?.current]);

  useEffect(() => {
    const lastMessageOrigin = messages?.[messages?.length - 1]?.origin;
    if (!isScrollToBottom && lastMessageOrigin === GLYMPSE_PRO_CHAT_ORIGIN) {
      scrollToBottomOfList();
    } else if (isScrollToBottom) {
      scrollToBottomOfList();
    }
  }, [isScrollToBottom, messages]);

  useLayoutEffect(() => {
    if (!isEmpty(messagesGroupedByDate) && isFirstOpen) {
      setTimeout(() => {
        messagesEndRef?.current!.scrollIntoView!({
          behavior: "auto",
          block: "end"
        });
      });
      setIsFirstOpen(false);
    }
  }, [messagesGroupedByDate, isFirstOpen]);

  return (
    <div className="message-list-container" ref={containerRef}>
      {
        !isEmpty(messagesGroupedByDate) ? (
          <div className="message-list">
            {
              keys(messagesGroupedByDate).map((date: string) => {
                const msgs = messagesGroupedByDate[date];

                return (
                  <React.Fragment key={date}>
                    {
                      msgs.map((message: any) => getMessageElement(message))
                    }
                  </React.Fragment>
                );
              })
            }
          </div>
        ) : (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center font-weight-bold">
            This chat is empty!
          </div>
        )
      }
      <div ref={messagesEndRef}/>
      {(!isScrollToBottom && !isFirstOpen) && <ScrollToBottomButton containerRef={messagesEndRef} />}
    </div>
  );
}


export default MessageList;
